// Render the saved-apps watchlist section and the pin toggle in the
// app-summary header (issue #18).

import { getWatchlist, isWatching, addToWatchlist, removeFromWatchlist } from '../domain/watchlist.js';
import { t } from '../i18n/index.js';

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      default: return '&#39;';
    }
  });
}

/**
 * Render (or re-render) the watchlist cards section.
 * @param {(trackId: string) => void} onSelect - Called when a card is clicked.
 */
export function renderWatchlist(onSelect) {
  const section = document.getElementById('watchlist');
  if (!section) return;

  const apps = getWatchlist();

  if (apps.length === 0) {
    section.hidden = true;
    section.innerHTML = '';
    return;
  }

  const cards = apps
    .map((app) => {
      const date = new Date(app.lastCompared).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
      const cardAriaLabel = escapeHtml(t('a11y.watchlistCard', { name: app.trackName }));
      return `<li>
        <button class="watchlist__card" type="button" data-track-id="${escapeHtml(String(app.trackId))}" aria-label="${cardAriaLabel}">
          ${app.artworkUrl ? `<img class="watchlist__art" src="${escapeHtml(app.artworkUrl)}" alt="" width="36" height="36" loading="lazy" />` : ''}
          <span class="watchlist__name" aria-hidden="true">${escapeHtml(app.trackName)}</span>
          <span class="watchlist__date" aria-hidden="true">${escapeHtml(date)}</span>
        </button>
      </li>`;
    })
    .join('');

  section.hidden = false;
  section.innerHTML = `<p class="watchlist__title">${escapeHtml(t('watchlist.title'))}</p><ul class="watchlist__cards">${cards}</ul>`;

  section.querySelectorAll('.watchlist__card').forEach((btn) => {
    btn.addEventListener('click', () => {
      onSelect(btn.dataset.trackId);
    });
  });
}

/**
 * Render or update the pin toggle button inside #app-summary.
 * Attaches a delegated click handler so re-renders don't stack listeners.
 *
 * @param {{ trackId: number|string, trackName: string, artworkUrl100?: string, artworkUrl60?: string }} app
 * @param {() => void} onToggle - Called after the state flips so callers can re-render.
 */
export function renderPinButton(app, onToggle) {
  const summary = document.getElementById('app-summary');
  if (!summary) return;

  // Remove any existing pin button to avoid duplicates on repeat comparisons.
  summary.querySelector('.pin-btn')?.remove();

  const watching = isWatching(app.trackId);
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `pin-btn${watching ? ' pin-btn--active' : ''}`;
  btn.dataset.trackId = String(app.trackId);
  btn.setAttribute('aria-pressed', String(watching));
  btn.textContent = watching ? t('watchlist.unpin') : t('watchlist.pin');

  btn.addEventListener('click', () => {
    if (isWatching(app.trackId)) {
      removeFromWatchlist(app.trackId);
    } else {
      addToWatchlist(app);
    }
    onToggle();
  });

  summary.appendChild(btn);
}
